import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Users, UserPlus, Loader2, Trash2, Copy, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [loading, setLoading] = useState(false);
  const [enroll, setEnroll] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setEnroll(data);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore creazione utente");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setEnroll(null);
    setForm({ name: "", email: "", password: "", role: "user" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button data-testid="open-create-user" className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="w-4 h-4 mr-2" /> Nuovo utente
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="create-user-dialog">
        {!enroll ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Crea un nuovo utente</DialogTitle>
              <DialogDescription>Compila i campi per creare un account. Verrà generato un QR per il 2FA.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label>Nome</Label>
                <Input data-testid="create-user-name" value={form.name} required
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" className="mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="create-user-email" type="email" value={form.email} required
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@86bit.it" className="mt-1" />
              </div>
              <div>
                <Label>Password (min 8 caratteri)</Label>
                <Input data-testid="create-user-password" type="text" value={form.password} required minLength={8}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-1" />
              </div>
              <div>
                <Label>Ruolo</Label>
                <select data-testid="create-user-role" value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                  <option value="user">Utente</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading} data-testid="submit-create-user"
                  className="bg-blue-600 hover:bg-blue-700">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea utente
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" /> Configura il 2FA
              </DialogTitle>
              <DialogDescription>Mostra questi dati all'utente: serviranno per configurare l'autenticazione a due fattori.</DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <p className="text-sm text-slate-500 mb-4">
                Comunica questi dati all'utente. Dovrà scansionare il QR con un'app di autenticazione
                (Google/Microsoft Authenticator). Questa chiave è mostrata <b>una sola volta</b>.
              </p>
              <div className="flex justify-center mb-4">
                <img src={enroll.qr_code} alt="QR 2FA" data-testid="created-user-qr"
                  className="w-44 h-44 border border-slate-200 rounded-lg" />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <p className="text-xs text-slate-500 mb-1">Chiave segreta (inserimento manuale):</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm font-mono break-all" data-testid="created-user-secret">{enroll.totp_secret}</code>
                  <button onClick={() => { navigator.clipboard.writeText(enroll.totp_secret); toast.success("Chiave copiata"); }}
                    className="text-slate-500 hover:text-blue-600 shrink-0">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={close} data-testid="close-enroll" className="bg-blue-600 hover:bg-blue-700">Fatto</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { password: pw });
      toast.success("Password aggiornata");
      setOpen(false);
      setPw("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`reset-pw-${user.id}`} title="Reimposta password">
          <KeyRound className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Reimposta password — {user.name}</DialogTitle>
          <DialogDescription>Imposta una nuova password per questo utente (minimo 8 caratteri).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label>Nuova password (min 8 caratteri)</Label>
            <Input data-testid="reset-pw-input" type="text" value={pw} required minLength={8}
              onChange={(e) => setPw(e.target.value)} className="mt-1" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="submit-reset-pw" className="bg-blue-600 hover:bg-blue-700">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aggiorna
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleActive = async (u, value) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: value });
      toast.success(value ? "Account attivato" : "Account disattivato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success("Ruolo aggiornato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" /> Gestione utenti
          </h1>
          <p className="text-slate-500 mt-1">Crea account, assegna ruoli e gestisci gli accessi.</p>
        </div>
        <CreateUserDialog onCreated={load} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="users-table">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-5 py-3 font-bold">Utente</th>
                <th className="text-left px-5 py-3 font-bold">Ruolo</th>
                <th className="text-left px-5 py-3 font-bold">Stato</th>
                <th className="text-right px-5 py-3 font-bold">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isSelf = me?.id === u.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-900">{u.name} {isSelf && <span className="text-xs text-slate-400">(tu)</span>}</p>
                      <p className="text-slate-500">{u.email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <select value={u.role} disabled={isSelf} data-testid={`role-select-${u.email}`}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className="h-9 rounded-md border border-slate-200 px-2 text-sm disabled:opacity-50">
                        <option value="user">Utente</option>
                        <option value="admin">Amministratore</option>
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={u.is_active} disabled={isSelf} data-testid={`active-switch-${u.email}`}
                          onCheckedChange={(v) => toggleActive(u, v)} />
                        <Badge variant={u.is_active ? "default" : "secondary"}
                          className={u.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>
                          {u.is_active ? "Attivo" : "Disattivato"}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <ResetPasswordDialog user={u} onDone={load} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" disabled={isSelf} data-testid={`delete-user-${u.email}`}
                              className="text-slate-500 hover:text-red-600 disabled:opacity-30">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminare {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                L'account e tutte le sue sessioni verranno rimossi. L'operazione è irreversibile.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u)} data-testid={`confirm-delete-${u.email}`}
                                className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
